import { put, select, takeEvery } from 'redux-saga/effects';
import { safeSaga } from 'store';
import {
    RESET_DECLARATION_AND_ENDORSEMENT,
    SET_DECLARATION_AND_ENDORSEMENT,
    UPDATE_COI_INVOLVED,
    SET_APPLICATION_DETAIL_POST_DATA,
    UPDATE_DECLARATION_NORMAL_POST_DATA,
    DELETE_DECLARATION_NORMAL_DATA,
    UPDATE_DECLARATION_STUDENT_POST_DATA,
    UPDATE_DECLARE_STUDENT,
} from 'store/key';
import { produce } from 'utility/general';
import { declareInvAutoFilledRoles, declareStudentAutoFilledRoles } from 'utility/formValidate';
import {
    declareStudentKey,
    declareInvKey,
    HAEndorsementKey,
    universitiesEndorsementKey,
    HAUniversitiesEndorsementKey,
} from 'constants/globalEnum';

const getInitialState = () => ({
    declareStudentRows: [],
    declareInvRows: [],
    HAEndorsementRows: [],
    universitiesEndorsementRows: [],
    HAUniversitiesEndorsementRows: [],
    unSaveDeletionIds: [] /** id: un-save & deleted & existedInDB row's id */,
    removeRequestCoiIds: [] /** id: remove request coi row's id */,
    unSaveDecalreStudentRows: [] /** rows: deleted but unSave records for decalreStudentRows */,
    AcademicSvTextFieldOnBlur: false /** use for Declaration Academic Supervisor "auto-filled" when Academic Supervisor's textField onBlur */,
    SiteSvTextFieldOnBlur: false /** use for Declaration Site Supervisor "auto-filled" when Site Supervisor's textField onBlur */,

    declareInvDefaultRow: {
        role: '',
        title: '',
        name: '',
        position: '',
        responsibility: '',
        signDate: null,
    },
    declareStudentDefaultRow: {
        role: '',
        title: '',
        name: '',
        position: '',
        responsibility: '',
        signDate: null,
    },
    /** update when student check/uncheck */
    postDeclareStudentRows: [],
    /** update when coiInvolved check/uncheck */
    postCoiRows: [],
});

function* updateDeclareStudent(action) {
    const { declarationAndEndorsement, applicationDetailPostData, applicationDetail } = yield select();

    if (action.payload === undefined) return;

    const { student, formId, siteInfoId } = action.payload;
    if (student === undefined) return;

    applicationDetailPostData.applicationFormId = formId;
    const postData = JSON.parse(JSON.stringify(applicationDetailPostData.postData));

    /** un-save data */
    const postDeclareStudentRows = postData?.declareStudent?.filter((item) => item.siteInfo?.id === siteInfoId) ?? [];
    const postDeclareStudentIds = postDeclareStudentRows?.map((item) => item.id) ?? []; // To be re-open futher

    /** records in db */
    const uploadDeclareStudentInDb =
        applicationDetail[formId].declareStudent?.filter((item) => item.siteInfoId === siteInfoId) ?? [];
    const declareStudentDeleteRequestIds =
        uploadDeclareStudentInDb
            ?.filter((item) => !declarationAndEndorsement.unSaveDeletionIds.includes(item.id))
            ?.map((item) => item.id) ?? [];

    const currentDeleteRequest = applicationDetailPostData?.deleteRequest?.declareStudent ?? [];
    const currentDeleteRequestIds = currentDeleteRequest?.map((item) => item.id) ?? [];

    if (student) {
        /** re-add hidden data for postData */
        // To be re-open futher
        const newRows = declarationAndEndorsement.postDeclareStudentRows;
        newRows?.forEach((item) => {
            if (!postDeclareStudentIds.includes(item.id)) {
                if (!applicationDetailPostData.postData.declareStudent) {
                    applicationDetailPostData.postData.declareStudent = [];
                }
                applicationDetailPostData.postData.declareStudent?.push(item);
            }
        });

        /** remove records which exist in DB to deleteRequest */
        declareStudentDeleteRequestIds?.forEach((id) => {
            if (currentDeleteRequestIds.includes(id)) {
                const index = currentDeleteRequest.findIndex((item) => item.id === id);
                if (applicationDetailPostData.deleteRequest.declareStudent?.length) {
                    applicationDetailPostData.deleteRequest.declareStudent.splice(index, 1);
                }
            }
        });
        if (
            applicationDetailPostData.deleteRequest.declareStudent &&
            !applicationDetailPostData.deleteRequest.declareStudent.length
        ) {
            delete applicationDetailPostData.deleteRequest.declareStudent;
        }
        declarationAndEndorsement.postDeclareStudentRows = [];
    } else {
        /** hidden(delete) data for postData */
        postDeclareStudentIds?.forEach((id) => {
            if (applicationDetailPostData.postData.declareStudent?.length) {
                const index = applicationDetailPostData.postData.declareStudent.findIndex((row) => row.id === id);
                if (index === -1) return;
                applicationDetailPostData.postData.declareStudent.splice(index, 1);
            }
        });
        if (
            applicationDetailPostData.postData.declareStudent &&
            !applicationDetailPostData.postData.declareStudent.length
        ) {
            delete applicationDetailPostData.postData.declareStudent;
        }

        /** add records which exist in DB to deleteRequest */
        declareStudentDeleteRequestIds?.forEach((id) => {
            if (!applicationDetailPostData.deleteRequest.declareStudent) {
                applicationDetailPostData.deleteRequest.declareStudent = [];
            }
            if (!currentDeleteRequestIds.includes(id)) {
                applicationDetailPostData.deleteRequest.declareStudent.push({ id: id, siteInfo: { id: siteInfoId } });
            }
        });

        declarationAndEndorsement.postDeclareStudentRows = postDeclareStudentRows;
    }

    yield put({
        type: SET_APPLICATION_DETAIL_POST_DATA,
        payload: applicationDetailPostData,
    });
    yield put({
        type: SET_DECLARATION_AND_ENDORSEMENT,
        payload: declarationAndEndorsement,
    });
}

function* updateCoiInvolved(action) {
    const { declarationAndEndorsement, applicationDetailPostData, applicationDetail } = yield select();

    if (action.payload === undefined) return;

    const { coiInvolved, formId, siteInfoId } = action.payload;
    if (coiInvolved === undefined) return;

    applicationDetailPostData.applicationFormId = formId;
    const postData = JSON.parse(JSON.stringify(applicationDetailPostData.postData));

    /** un-save data */
    const postDeclareInv = postData?.declareInv?.filter((item) => item.siteInfo?.id === siteInfoId) ?? [];
    const roleName = declareInvAutoFilledRoles[1];
    const postCoiRows = postDeclareInv?.filter((item) => item.role === roleName) ?? [];
    const postCoiIds = postCoiRows?.map((item) => item.id) ?? [];
    const postDeclareInvIds = postDeclareInv?.map((item) => item.id) ?? []; // To be re-open futher

    /** records in db */
    const uploadDeclareInvInDb =
        applicationDetail[formId].declareInv?.filter((item) => item.siteInfoId === siteInfoId) ?? [];
    const coiDeleteRequestIds =
        uploadDeclareInvInDb
            ?.filter((item) => item.role === roleName && !declarationAndEndorsement.unSaveDeletionIds.includes(item.id))
            ?.map((item) => item.id) ?? [];

    const currentDeleteRequest = applicationDetailPostData?.deleteRequest?.declareInv ?? [];
    const currentDeleteRequestIds = currentDeleteRequest?.map((item) => item.id) ?? [];

    if (coiInvolved) {
        /** re-add hidden data for postData */
        // To be re-open futher
        declarationAndEndorsement.postCoiRows?.forEach((item) => {
            if (!postDeclareInvIds.includes(item.id)) {
                if (!applicationDetailPostData.postData.declareInv) {
                    applicationDetailPostData.postData.declareInv = [];
                }
                applicationDetailPostData.postData.declareInv?.push(item);
            }
        });

        /** remove records which exist in DB to deleteRequest */
        coiDeleteRequestIds?.forEach((id) => {
            if (currentDeleteRequestIds.includes(id)) {
                const index = currentDeleteRequest.findIndex((item) => item.id === id);
                if (applicationDetailPostData.deleteRequest.declareInv?.length) {
                    applicationDetailPostData.deleteRequest.declareInv.splice(index, 1);
                }
            }
        });
        if (
            applicationDetailPostData.deleteRequest.declareInv &&
            !applicationDetailPostData.deleteRequest.declareInv.length
        ) {
            delete applicationDetailPostData.deleteRequest.declareInv;
        }
        declarationAndEndorsement.postCoiRows = [];
    } else {
        /** hidden(delete) data for postData */
        postCoiIds?.forEach((id) => {
            if (applicationDetailPostData.postData.declareInv?.length) {
                const index = applicationDetailPostData.postData.declareInv.findIndex((row) => row.id === id);
                if (index === -1) return;
                applicationDetailPostData.postData.declareInv.splice(index, 1);
            }
        });
        if (applicationDetailPostData.postData.declareInv && !applicationDetailPostData.postData.declareInv.length) {
            delete applicationDetailPostData.postData.declareInv;
        }

        /** add records which exist in DB to deleteRequest */
        coiDeleteRequestIds?.forEach((id) => {
            if (!applicationDetailPostData.deleteRequest.declareInv) {
                applicationDetailPostData.deleteRequest.declareInv = [];
            }
            if (!currentDeleteRequestIds.includes(id)) {
                applicationDetailPostData.deleteRequest.declareInv.push({ id: id, siteInfo: { id: siteInfoId } });
            }
        });

        declarationAndEndorsement.postCoiRows = postCoiRows;
    }

    yield put({
        type: SET_APPLICATION_DETAIL_POST_DATA,
        payload: applicationDetailPostData,
    });
    yield put({
        type: SET_DECLARATION_AND_ENDORSEMENT,
        payload: declarationAndEndorsement,
    });
}

function* updatePostDataList(action) {
    const { applicationDetailPostData } = yield select();

    const { rows, type, formId, siteInfoId } = action.payload;

    if (!rows.length) return;

    applicationDetailPostData.applicationFormId = formId;

    const newPostData = JSON.parse(JSON.stringify(applicationDetailPostData.postData));
    const postDeclareStudent = newPostData.declareStudent ?? [];
    const postDeclareInv = newPostData.declareInv ?? [];
    const postDeclare = newPostData.declare ?? [];

    let key = 'declare';
    let postData = postDeclare;

    if (type === declareInvKey) {
        key = declareInvKey;
        postData = postDeclareInv;
    } else if (type === declareStudentKey) {
        key = declareStudentKey;
        postData = postDeclareStudent;
    }

    let requestData = rows.map((item) => {
        let row = {
            ...item,
            siteInfo: { id: siteInfoId },
        };
        if (item.signDate) {
            row.signDate = new Date(item.signDate);
        }

        if (type !== declareInvKey && type !== declareStudentKey) {
            row.endorse = type;
        }

        /** delete record when repeated */
        const existedIndex = postData.findIndex((i) => i?.id === item?.id);
        if (existedIndex !== -1) {
            postData.splice(existedIndex, 1);
        }

        return row;
    });

    applicationDetailPostData.postData[key] = [...postData, ...requestData];

    yield put({
        type: SET_APPLICATION_DETAIL_POST_DATA,
        payload: applicationDetailPostData,
    });
}

/** check if need to auto-filled for investigators */
const checkNotAutoFilled = (roleName, list) => {
    let notFilled = true;
    for (let i of list) {
        if (roleName.toUpperCase() === i.role.toUpperCase()) {
            notFilled = false;
            break;
        }
    }
    return notFilled;
};

function* updateDeclarationStudentPostData(action) {
    const { declarationAndEndorsement } = yield select();

    if (action.payload === undefined) return;
    const { formId, siteInfoId, roleNameIndex, supervisorName, siteSupervisorName } = action.payload;

    const currentSiteDeclareStudentRows = declarationAndEndorsement.declareStudentRows.filter(
        (item) => item.siteInfo?.id === siteInfoId,
    );
    /** if PI investigator is existed, break */
    if (!checkNotAutoFilled(declareStudentAutoFilledRoles[roleNameIndex], currentSiteDeclareStudentRows)) return;

    const list = declarationAndEndorsement.declareStudentRows;
    let newRows = [];
    const key = 'temp_';
    switch (roleNameIndex) {
        case 0:
            /** P2.3.2: Auto-filled when p2.3.2 completed-filled */
            newRows.push({
                ...declarationAndEndorsement.declareStudentDefaultRow,
                role: declareStudentAutoFilledRoles[0],
                id: key + 'p232',
                name: supervisorName,
                siteInfo: { id: siteInfoId },
            });
            break;

        case 1:
            /** P2.3.3: Auto-filled when p2.3.3 completed-filled */
            newRows.push({
                ...declarationAndEndorsement.declareStudentDefaultRow,
                role: declareStudentAutoFilledRoles[1],
                id: key + 'p233',
                name: siteSupervisorName,
                siteInfo: { id: siteInfoId },
            });
            break;

        default:
            break;
    }

    if (newRows.length) {
        const rows = [...list, ...newRows];
        const seenSiteInfoIds = new Set();
        const firstPIs = [];

        rows.forEach((entry) => {
            if (entry.role === 'Principal Investigator' && !seenSiteInfoIds.has(entry.siteInfoId)) {
                seenSiteInfoIds.add(entry.siteInfoId);
                firstPIs.push(entry);
            }
        });
        declarationAndEndorsement.declareStudentRows = firstPIs;
        yield updatePostDataList({
            payload: { rows: newRows, type: declareStudentKey, siteInfoId, formId },
        });

        yield put({
            type: SET_DECLARATION_AND_ENDORSEMENT,
            payload: declarationAndEndorsement,
        });
    }
}

function* updateDeclarationNormalPostData(action) {
    const { declarationAndEndorsement } = yield select();

    if (action.payload === undefined) return;
    const { formId, siteInfoId, row, roleNameIndex, firstName, surname, title } = action.payload;

    const currentSiteDeclareInvRows = declarationAndEndorsement.declareInvRows.filter(
        (item) => item.siteInfo?.id === siteInfoId,
    );
    /** if PI investigator is existed, break */
    if (roleNameIndex === 0 && !checkNotAutoFilled(declareInvAutoFilledRoles[0], currentSiteDeclareInvRows)) return;

    const list = declarationAndEndorsement.declareInvRows;
    let newRows = [];
    let coiList = [];
    const key = 'temp_';
    switch (roleNameIndex) {
        case 0:
            /** Auto-filled record refer from p1 (role, title, name) when p1 completed-filled */
            newRows.push({
                ...declarationAndEndorsement.declareInvDefaultRow,
                role: declareInvAutoFilledRoles[roleNameIndex],
                id: key + 'p1',
                name: `${firstName} ${surname}`,
                title: title,
                siteInfo: { id: siteInfoId },
            });
            break;

        case 1:
            /** Auto-filled records refer from p3 (Co-investigators) & when p3 add records */
            coiList.push({
                ...declarationAndEndorsement.declareInvDefaultRow,
                role: declareInvAutoFilledRoles[roleNameIndex],
                id: row.id,
                title: row.title,
                name: `${row.firstName} ${row.surname}`,
                siteInfo: { id: siteInfoId },
            });
            break;

        default:
            break;
    }

    if (newRows.length || coiList.length) {
        const rows = [...list, ...newRows, ...coiList];
        declarationAndEndorsement.declareInvRows = rows;

        yield updatePostDataList({
            payload: { rows: [...newRows, ...coiList], type: declareInvKey, siteInfoId, formId },
        });

        yield put({
            type: SET_DECLARATION_AND_ENDORSEMENT,
            payload: declarationAndEndorsement,
        });
    }
}

function* deleteTablePostDataRow(action) {
    const { declarationAndEndorsement, applicationDetailPostData, applicationDetail } = yield select();

    const { row, key, formId, siteInfoId, isHandleDelete } = action.payload;
    applicationDetailPostData.applicationFormId = formId;

    let newKey = 'declare';
    if (key !== 'declare') {
        newKey = key;
    }
    const list = applicationDetail[formId][newKey] ?? [];
    const isExistInDB = list.findIndex((item) => item.id === row.id) !== -1;

    if (isExistInDB) {
        /** add data to deleteRequest */
        if (applicationDetailPostData.deleteRequest[newKey] === undefined) {
            applicationDetailPostData.deleteRequest[newKey] = [];
        }
        applicationDetailPostData.deleteRequest[newKey].push({
            id: row.id,
            siteInfo: {
                id: siteInfoId,
            },
        });

        yield put({
            type: SET_APPLICATION_DETAIL_POST_DATA,
            payload: applicationDetailPostData,
        });
        // if (coiInvolved && key === declareInvKey) {
        //     dispatch({
        //         type: SET_DECLARATION_AND_ENDORSEMENT,
        //         payload: {
        //             removeRequestCoiIds: [...removeRequestCoiIds, row?.id],
        //         },
        //     });
        // }

        if (isHandleDelete) {
            declarationAndEndorsement.unSaveDeletionIds = [...declarationAndEndorsement.unSaveDeletionIds, row?.id];
            yield put({
                type: SET_DECLARATION_AND_ENDORSEMENT,
                payload: declarationAndEndorsement,
            });
        }
    } else {
        /** delete PostData */
        const postData = JSON.parse(JSON.stringify(applicationDetailPostData.postData));
        const newPostData = postData[newKey] ?? [];

        /** delete record when repeated */
        const existedIndex = newPostData.findIndex((i) => i?.id === row?.id);
        if (existedIndex !== -1) {
            newPostData.splice(existedIndex, 1);
            applicationDetailPostData.postData[newKey] = newPostData;
            yield put({
                type: SET_APPLICATION_DETAIL_POST_DATA,
                payload: applicationDetailPostData,
            });
        }
    }
}

function* deleteRow(action) {
    const { declarationAndEndorsement } = yield select();

    const { row, key, formId, siteInfoId } = action.payload;

    const typeList = {
        [declareStudentKey]: 'declareStudentRows',
        [declareInvKey]: 'declareInvRows',
        [HAEndorsementKey]: 'HAEndorsementRows',
        [universitiesEndorsementKey]: 'universitiesEndorsementRows',
        [HAUniversitiesEndorsementKey]: 'HAUniversitiesEndorsementRows',
    };
    const type = typeList[key];
    if (!row || !type) return;
    const list = declarationAndEndorsement[type];
    const rowsCopy = [...list];
    const index = list.findIndex((item, index) => {
        return item?.id === row?.id;
    });
    if (index !== -1) {
        yield deleteTablePostDataRow({
            payload: {
                row,
                key,
                formId,
                siteInfoId,
                isHandleDelete: true,
            },
        });

        /** delete record when repeated */
        rowsCopy.splice(index, 1);
        declarationAndEndorsement[type] = rowsCopy;

        yield put({
            type: SET_DECLARATION_AND_ENDORSEMENT,
            payload: declarationAndEndorsement,
        });
    }
}

/** delete coi record when co-i table had deletion */
function* deleteDeclarationNormalData(action) {
    const { declarationAndEndorsement } = yield select();

    if (action.payload === undefined) return;
    const { formId, siteInfoId, deletionCoiRowIndex } = action.payload;

    const list = declarationAndEndorsement.declareInvRows?.filter((item) => item.siteInfo.id === siteInfoId);

    let declareCoiList = [];
    list.forEach((item) => {
        if (item.role === declareInvAutoFilledRoles[1]) {
            declareCoiList.push(item);
        }
    });
    if (declareCoiList[deletionCoiRowIndex] !== undefined) {
        yield deleteRow({
            payload: {
                row: declareCoiList[deletionCoiRowIndex],
                key: declareInvKey,
                formId,
                siteInfoId,
            },
        });
    }
}

const reducer = (state = getInitialState(), action) => {
    switch (action.type) {
        case RESET_DECLARATION_AND_ENDORSEMENT:
            return getInitialState();
        case SET_DECLARATION_AND_ENDORSEMENT:
            const newState = produce(state, (draft) => {
                Object.entries(action.payload).forEach(([key, value]) => {
                    draft[key] = action.payload[key];
                });
            });
            return newState;
        default:
            return state;
    }
};

export function* watchDeclarationAndEndorsement() {
    yield takeEvery(UPDATE_COI_INVOLVED, safeSaga(updateCoiInvolved));
    yield takeEvery(UPDATE_DECLARATION_NORMAL_POST_DATA, safeSaga(updateDeclarationNormalPostData));
    yield takeEvery(UPDATE_DECLARATION_STUDENT_POST_DATA, safeSaga(updateDeclarationStudentPostData));
    yield takeEvery(DELETE_DECLARATION_NORMAL_DATA, safeSaga(deleteDeclarationNormalData));
    yield takeEvery(UPDATE_DECLARE_STUDENT, safeSaga(updateDeclareStudent));
}

export default reducer;
